import { Button, Icon } from 'components/common';
import styled, { css } from 'styled-components';
import { colours } from 'utils/theme';

// ========== Shared CSS ==========

const flexCenter = css`
  display: flex;
  align-items: center;
`;

const textBase = css`
  font-family: Roboto;
  font-weight: 400;
  font-style: Regular;
  font-size: 12px;
  leading-trim: NONE;
  line-height: 140%;
  letter-spacing: 0%;
  color: ${colours.text};
  vertical-align: center;
`;

// ========== Typography ==========

export const H2 = styled.h2`
  ${textBase};
  margin: 0;
  font-size: 16px;
  font-weight: 500;
`;

export const Paragraph = styled.p`
  ${textBase};
`;

export const Label = styled.label`
  ${textBase};
  line-height: 100%;
`;

export const ParagraphGrey = styled(Paragraph)`
  color: #16161680;
  margin: 0;
`;

// ========== Buttons ==========

export const ExportTemplateButton = styled(Button)`
  font-family: Roboto;
  line-height: 32px;
  text-align: center;
  ${flexCenter};
  gap: 10px;
  justify-content: center;
  padding: 4px 14px;
  border-radius: 4px;
  height: 32px;
`;

export const SmallButton = styled(ExportTemplateButton)`
  font-size: 12px;
`;

export const ActionButton = styled(Button)`
  width: 80px;
  height: 25px;
  font-size: 14px;
  border-radius: 2px;
`;

export const MoreButton = styled(Button)`
  height: 25px;
  width: 35px;
  color: ${colours.text};
  padding: 4px 8px;
  ${flexCenter};
  justify-content: center;
  border-radius: 2px;
`;

// ========== List Tab ==========

export const StarButton = styled.div`
  ${flexCenter};
  cursor: ${p => (p.disabled ? 'default' : 'pointer')};
  color: ${p => (p.active ? colours.gold : colours.lightGrey)};
  opacity: ${p => (p.disabled ? 0.6 : 1)};
  &:hover {
    color: ${p =>
      p.disabled
        ? p.active
          ? colours.gold
          : colours.lightGrey
        : colours.gold};
  }
`;

export const ActionsSpan = styled.span`
  ${flexCenter};
  gap: 8px;
  color: ${colours.text};
`;

export const ActionsIcon = styled(Icon)`
  width: 16px;
  color: ${colours.text};
`;

// ========== Modal Layout ==========

export const ModalHeader = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 15px 48px 15px 20px;
  border-bottom-width: 1px;
  font-weight: 500;
  font-size: 16px;
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 15px 20px;
  border-top: 1px solid ${colours.borderGrey};
`;

export const ModalContentSection = styled.div`
  gap: 16px;
  padding: 15px 20px;
  border-top: 1px solid ${colours.borderGrey};
`;

export const ModalList = styled.div`
  max-height: calc(80vh - 200px);
  overflow-y: auto;
  border-top: 1px solid ${colours.borderGreyLight};
`;

export const ModalListRow = styled.div`
  ${flexCenter};
  border-bottom: 1px solid ${colours.borderGreyLight};
  &:hover {
    background-color: #fbfbfb;
  }
  &:last-child {
    border-bottom: none;
  }
`;

export const TemplateSheetCheckboxContainer = styled.div`
  ${flexCenter};
  justify-content: center;
  width: 56px;
  height: 42px;
`;

export const TemplateSheetInfo = styled.div`
  flex: 1;
  padding: 10px 0;
`;

export const TemplateSheetFieldCount = styled.div`
  font-size: 10px;
  line-height: 16px;
  letter-spacing: 0.5px;
  color: #6f6f6f;
`;

export const TemplateSheetActions = styled.div`
  ${flexCenter};
  gap: 10px;
`;

// ========== Breadcrumbs ==========

export const BreadcrumbLink = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  color: #999;
  cursor: pointer;
  &:hover {
    color: ${colours.linkBlue};
  }
`;

export const BreadcrumbText = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  color: ${colours.text};
`;

// ========== Editor Rows ==========

export const EditorRow = styled.div`
  ${flexCenter};
  gap: 4px;
  ${p => p.$bold && 'font-weight: 500;'}
  ${p => p.$flex && 'flex: 1;'}
`;

export const ValidationError = styled.div`
  font-size: 8px;
  line-height: 16px;
  letter-spacing: 0.5px;
  color: #ff4d4f;
  margin-top: 4px;
`;

export const SmallText = styled.span`
  font-size: ${p => p.$size || 10}px;
  color: #8d8d8d;
  ${p => p.$mt && `margin-top: ${p.$mt}px;`}
`;

// ========== Revertable Input ==========

export const RevertableInputRoot = styled.span`
  display: flex;
  flex-direction: column;
  width: 300px;
  position: relative;
`;

export const RevertableInputRevertButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: all 0.3s;
  &:hover {
    color: ${colours.linkBlue};
  }
`;

export const RevertableInputCheckButton = styled.button`
  background: none;
  padding: 0;
  font: inherit;
  color: inherit;
  width: 38px;
  ${flexCenter};
  justify-content: center;
  border: 1px solid ${colours.borderGrey};
  border-top-right-radius: 2px;
  border-bottom-right-radius: 2px;
  margin-left: -1px;
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  transition: all 0.3s;
  &:hover {
    color: ${p => (p.disabled ? 'inherit' : colours.linkBlue)};
    border-color: ${p => (p.disabled ? colours.borderGrey : colours.linkBlue)};
    z-index: ${p => (p.disabled ? 0 : 1)};
  }
`;

// ========== Drag Handle ==========

export const DragRoot = styled.div`
  display: flex;
  flex-direction: column;
  ${flexCenter};
  justify-content: center;
  gap: 1.5px;
  margin-right: 10px;
  width: 42px;
  height: 42px;
  padding: 2px;
  color: #ccc;
  border-radius: 25%;
  cursor: grab;
  &:hover {
    background-color: #f9f9f9;
  }
  &:active {
    cursor: grabbing;
  }
`;

export const DragDots = styled.div`
  display: flex;
  gap: 1.5px;
  &::before,
  &::after {
    content: '';
    width: 3px;
    height: 3px;
    background: #8d8d8d;
    border-radius: 50%;
  }
`;

// ========== Summary (Save Template) ==========

export const SummaryBox = styled.div`
  flex: 1;
  padding: 24px 12px;
  border: 1px solid ${colours.borderGreyLight};
  border-radius: 8px;
  text-align: center;
`;

export const SummaryValue = styled.div`
  font-size: 35px;
  leading-trim: NONE;
  line-height: 22px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #666;
`;

export const SummaryLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 8px;
`;
